import { useState, useEffect } from 'react';
import { flows } from '../services/api';
import { FlowCompletionStatus } from '../types';

interface FlowStatus {
  completionStatus: FlowCompletionStatus | null;
  requirementsMet: boolean | null;
  lastUpdated: number;
}

interface FlowStatuses {
  [flowName: string]: FlowStatus;
}

const CACHE_DURATION = 30000; // Cache for 30 seconds
let statusCache: FlowStatuses = {};
let pendingRequests: { [key: string]: Promise<any> } = {};

interface UseFlowStatusesResult {
  statuses: FlowStatuses;
  loading: boolean;
  error: string | null;
  refetch: (flowNames?: string[]) => Promise<void>;
}

/**
 * Batches and caches flow status requests to minimize API calls
 * @param flowNames Array of flow names to check
 * @returns Object containing statuses for all requested flows
 */
export const useFlowStatuses = (flowNames: string[]): UseFlowStatusesResult => {
  const [statuses, setStatuses] = useState<FlowStatuses>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFlowStatus = async (flowName: string): Promise<FlowStatus> => {
    const now = Date.now();
    const cached = statusCache[flowName];

    // Return cached data if valid
    if (cached && (now - cached.lastUpdated) < CACHE_DURATION) {
      return cached;
    }

    // Check if there's already a pending request for this flow
    const pendingRequest = pendingRequests[flowName];
    if (pendingRequest) {
      await pendingRequest;
      return statusCache[flowName];
    }

    try {
      // Create promises for both requests
      const completionPromise = flows.getCompletionStatus(flowName);
      const requirementsPromise = flows.checkRequirementsMet(flowName);

      // Store the combined promise
      pendingRequests[flowName] = Promise.all([completionPromise, requirementsPromise]);

      // Wait for both requests to complete
      const [completionStatus, requirementsResponse] = await pendingRequests[flowName];

      // Create new status object
      const newStatus: FlowStatus = {
        completionStatus,
        requirementsMet: requirementsResponse?.requirements_met ?? null,
        lastUpdated: now
      };

      // Update cache
      statusCache[flowName] = newStatus;

      return newStatus;
    } catch (err) {
      console.error(`Error fetching status for flow ${flowName}:`, err);
      throw err;
    } finally {
      // Clean up pending request
      delete pendingRequests[flowName];
    }
  };

  const fetchAllStatuses = async (targetFlows?: string[]) => {
    const flowsToFetch = targetFlows || flowNames;
    if (!flowsToFetch.length) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch all flow statuses in parallel
      const results = await Promise.all(
        flowsToFetch.map(async (flowName) => {
          try {
            const status = await fetchFlowStatus(flowName);
            return [flowName, status] as [string, FlowStatus];
          } catch (err) {
            console.error(`Error fetching status for ${flowName}:`, err);
            return [flowName, null] as [string, null];
          }
        })
      );

      // Update state with all results
      const newStatuses = { ...statuses };
      results.forEach(([flowName, status]) => {
        if (status) {
          newStatuses[flowName] = status;
        }
      });

      setStatuses(newStatuses);
    } catch (err) {
      console.error('Error fetching flow statuses:', err);
      setError('Failed to fetch flow statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStatuses();
  }, [JSON.stringify(flowNames)]); // Only refetch if flowNames array changes

  const refetch = async (targetFlows?: string[]) => {
    // Clear cache for specified flows or all flows
    if (targetFlows) {
      targetFlows.forEach(flowName => {
        delete statusCache[flowName];
      });
    } else {
      statusCache = {};
    }
    await fetchAllStatuses(targetFlows);
  };

  return {
    statuses,
    loading,
    error,
    refetch
  };
};

export default useFlowStatuses;
